import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  IconButton,
  Textarea,
  Input,
  Select,
  Option,
  FormControl,
  FormLabel,
  Stack
} from '@mui/joy';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import JsonStructureEditor from './JsonStructureEditor';
import JobConfigEditor from './JobConfigEditor';

/**
 * Reusable form field renderer component
 * Handles different field types: table, textarea, input, number, select, multi-select, checkbox, array
 */
const RenderField = React.memo(({ type, value, onChange, options = [] }) => {
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);

  switch (type) {
    case 'table':
      if (fieldDialogOpen) {
        return (
          <Modal
            open
            onClose={() => setFieldDialogOpen(false)}
          >
            <ModalDialog
              sx={{
                width: "70%",
                p: 4,
              }}
            >
              <JsonStructureEditor
                onChange={onChange}
                value={value}
                onClose={() => setFieldDialogOpen(false)}
              />
            </ModalDialog>
          </Modal>
        );
      } else {
        return (
          <div style={{ overflow: "hidden" }}>
            <IconButton onClick={() => setFieldDialogOpen(true)}>
              <OpenInFullIcon />
            </IconButton>
          </div>
        );
      }

    case 'textarea':
      return (
        <Textarea
          minRows={6}
          maxRows={10}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
        />
      );

    case 'yaml-editor':
      return (
        <JobConfigEditor
          value={value}
          onChange={onChange}
        />
      );

    case 'input':
      return (
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      );

    case 'number':
      return (
        <Input
          type='number'
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          slotProps={{
            input: {
              min: 1
            }
          }}
        />
      );

    case 'select':
      return (
        <Select
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
        >
          {options.map(option => {
            const optValue = typeof option === 'object' ? option.value : option;
            const optLabel = typeof option === 'object' ? option.label : option;
            return (
              <Option key={optValue} value={optValue}>
                {optLabel}
              </Option>
            );
          })}
        </Select>
      );

    case "multi-select":
      return (
        <Select
          value={value}
          multiple
          onChange={(_, newValue) => onChange(newValue)}
        >
          {options.map(option => {
            const optValue = typeof option === 'object' ? option.value : option;
            const optLabel = typeof option === 'object' ? option.label : option;
            return (
              <Option key={optValue} value={optValue}>
                {optLabel}
              </Option>
            );
          })}
        </Select>
      );

    case "checkbox":
      return (
        <div style={{ display: "inline-block" }}>
          <button
            style={{
              padding: "0.4rem 1.2rem",
              backgroundColor: value ? "#4CAF50" : "#f44336",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              width: "auto",
              display: "inline-block",
              fontWeight: "bold",
              whiteSpace: "nowrap"
            }}
            onClick={() => onChange(!value)}
          >
            {value ? "Enabled" : "Disabled"}
          </button>
        </div>
      );

    case "array":
      return (
        <Stack direction="row" spacing={2}>
          {["x1", "y1", "x2", "y2"].map((label, idx) => (
            <FormControl key={label} sx={{ minWidth: 90 }}>
              <FormLabel>{label}</FormLabel>
              <Input
                type="number"
                value={Array.isArray(value) ? value[idx] : ""}
                onChange={e => {
                  const arr = Array.isArray(value) ? [...value] : [0, 0, 0, 0];
                  arr[idx] = Number(e.target.value);
                  onChange(arr);
                }}
                placeholder={label}
                sx={{ width: 80 }}
              />
            </FormControl>
          ))}
        </Stack>
      );

    default:
      return null;
  }
});

RenderField.displayName = 'RenderField';

export default RenderField;
